import React, { useCallback, useEffect, useState } from 'react';
import { Alert, alpha, Box, Button, CircularProgress, Stack, TextField, Typography } from '@mui/material';
import {
  addBlacklistWatch, checkBlacklist, checkBlacklistWatch, listBlacklistEvents, listBlacklistWatches, removeBlacklistWatch, updateBlacklistWatch
} from 'api/intel';
import { GREEN, MONO, SEV_COLOR } from './scanUi';

const panel = { borderRadius: 3, border: 1, borderColor: 'divider', bgcolor: 'background.paper' };
const Pill = ({ color, children }) => <Typography component="span" sx={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.4, px: 1.3, py: 0.5, borderRadius: 99, bgcolor: alpha(color, 0.16), color, whiteSpace: 'nowrap' }}>{children}</Typography>;

function IpResult({ r }) {
  const clean = !r.listed.length;
  const verdict = r.listed.length ? { color: '#ef4444', text: `Listed on ${r.listed.length} blacklist${r.listed.length === 1 ? '' : 's'}` }
    : r.inconclusive ? { color: '#f59e0b', text: 'Not listed, but the result cannot be trusted' }
      : { color: GREEN, text: 'Not listed' };
  return (
    <Box sx={{ ...panel, overflow: 'hidden' }}>
      <Stack direction="row" alignItems="center" gap={1.5} flexWrap="wrap" sx={{ p: 2.2, background: `linear-gradient(135deg, ${alpha(verdict.color, 0.1)}, transparent 70%)` }}>
        <Typography sx={{ fontFamily: MONO, fontWeight: 700 }}>{r.ip}</Typography>
        <Pill color={verdict.color}>{verdict.text.toUpperCase()}</Pill>
        <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>{r.lists_checked - r.no_answer.length} of {r.lists_checked} lists gave a trustworthy answer</Typography>
      </Stack>
      {r.listed.map((l) => (
        <Stack key={l.zone} direction="row" alignItems="center" gap={2} flexWrap="wrap" sx={{ px: 2.8, py: 1.4, borderTop: 1, borderColor: 'divider' }}>
          <Typography sx={{ flex: '1 1 160px', fontWeight: 600 }}>{l.list}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ flex: '2 1 260px' }}>{l.meaning}</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ fontFamily: MONO }}>{l.codes.join(', ')}</Typography>
          <Pill color={SEV_COLOR[l.severity] || '#64748b'}>{l.severity.toUpperCase()}</Pill>
        </Stack>
      ))}
      {clean && !r.inconclusive && <Typography color="text.secondary" variant="body2" sx={{ px: 2.8, py: 1.6, borderTop: 1, borderColor: 'divider' }}>No blacklist that answered lists this address.</Typography>}
      {r.no_answer.length > 0 && (
        <Box sx={{ px: 2.8, py: 1.6, borderTop: 1, borderColor: 'divider' }}>
          <Typography variant="body2" color="text.secondary">
            No answer from: {r.no_answer.join(', ')}. These refused this server or did not respond, so they are not counted as "not listed".
            {r.inconclusive && ' Spamhaus refuses public resolvers such as Google and Cloudflare; run the server with its own resolver.'}
          </Typography>
        </Box>
      )}
    </Box>
  );
}


const STATE = {
  clean: ['#22c55e', 'NOT LISTED'],
  listed: ['#ef4444', 'LISTED'],
  inconclusive: ['#f59e0b', 'NOT SURE'],
  unreachable: ['#94a3b8', 'NO ADDRESS FOUND']
};
const ago = (iso) => {
  if (!iso) return 'not checked yet';
  const mins = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 60000));
  if (mins < 60) return `${mins} min ago`;
  if (mins < 1440) return `${Math.floor(mins / 60)} h ago`;
  return `${Math.floor(mins / 1440)} d ago`;
};
const fail = (e, fallback) => e?.response?.data?.host?.[0] || e?.message || fallback;

function Watching() {
  const [rows, setRows] = useState(null);
  const [events, setEvents] = useState([]);
  const [host, setHost] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      const [w, e] = await Promise.all([listBlacklistWatches(), listBlacklistEvents()]);
      setRows(w?.results || w || []);
      setEvents(e?.results || []);
    } catch (err) { setError(fail(err, 'Could not load the watch list.')); setRows([]); }
  }, []);
  useEffect(() => { load(); }, [load]);
  // The first check of a new address runs in the background: look again shortly.
  useEffect(() => {
    if (!(rows || []).some((r) => !r.last_checked_at)) return undefined;
    const t = setTimeout(load, 6000);
    return () => clearTimeout(t);
  }, [rows, load]);

  const add = async (e) => {
    e.preventDefault();
    if (!host.trim() || busy) return;
    setBusy(true); setError('');
    try { await addBlacklistWatch(host.trim()); setHost(''); await load(); } catch (err) { setError(fail(err, 'Could not add it.')); } finally { setBusy(false); }
  };
  const act = async (fn) => { setError(''); try { await fn(); await load(); } catch (err) { setError(fail(err, 'That did not work.')); } };

  return (
    <Stack spacing={2}>
      <Box>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>Watch these every day</Typography>
        <Typography color="text.secondary" variant="body2">Add your mail servers once. You get an email only when one becomes listed or is cleared.</Typography>
      </Box>
      {error && <Alert severity="error" onClose={() => setError('')}>{error}</Alert>}
      <Box component="form" onSubmit={add} sx={{ ...panel, p: 2.2 }}>
        <Stack direction="row" gap={1.5} flexWrap="wrap">
          <TextField size="small" value={host} onChange={(e) => setHost(e.target.value)} placeholder="mail.example.com or 203.0.113.7" sx={{ flex: '1 1 320px' }} inputProps={{ 'aria-label': 'Mail server or IP address to watch' }} />
          <Button type="submit" variant="outlined" disabled={busy || !host.trim()} sx={{ textTransform: 'none', fontWeight: 700, color: GREEN, borderColor: alpha(GREEN, 0.5) }}>{busy ? 'Adding…' : 'Watch it'}</Button>
        </Stack>
      </Box>

      {rows && (
        <Box sx={{ ...panel, overflow: 'hidden' }}>
          {!rows.length && <Typography color="text.secondary" variant="body2" sx={{ p: 2.8 }}>Nothing is watched yet.</Typography>}
          {rows.map((r, i) => {
            const [color, text] = STATE[r.last_state] || ['#94a3b8', 'CHECKING…'];
            return (
              <Box key={r.id} sx={{ px: 2.8, py: 1.6, borderTop: i ? 1 : 0, borderColor: 'divider', opacity: r.enabled ? 1 : 0.6 }}>
                <Stack direction="row" alignItems="center" gap={2} flexWrap="wrap">
                  <Box sx={{ flex: '2 1 240px', minWidth: 0 }}>
                    <Typography sx={{ fontFamily: MONO, fontWeight: 600, wordBreak: 'break-all' }}>{r.host}</Typography>
                    <Typography variant="caption" color="text.secondary">{r.enabled ? `checked ${ago(r.last_checked_at)}` : 'paused'}</Typography>
                  </Box>
                  <Pill color={color}>{r.last_state === 'listed' ? `LISTED ON ${r.last_listed.length}` : text}</Pill>
                  <Button size="small" onClick={() => act(() => checkBlacklistWatch(r.id))} disabled={!r.enabled} sx={{ textTransform: 'none', color: GREEN, fontWeight: 700 }}>Check now</Button>
                  <Button size="small" color="inherit" onClick={() => act(() => updateBlacklistWatch(r.id, { enabled: !r.enabled }))} sx={{ textTransform: 'none' }}>{r.enabled ? 'Pause' : 'Resume'}</Button>
                  <Button size="small" color="error" onClick={() => act(() => removeBlacklistWatch(r.id))} sx={{ textTransform: 'none' }}>Remove</Button>
                </Stack>
                {r.last_state === 'listed' && r.last_listed.map((l) => (
                  <Stack key={`${l.ip}-${l.zone}`} direction="row" alignItems="center" gap={1.5} flexWrap="wrap" sx={{ mt: 0.8, pl: 1.5 }}>
                    <Pill color={SEV_COLOR[l.severity] || '#64748b'}>{String(l.severity).toUpperCase()}</Pill>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{l.list}</Typography>
                    <Typography variant="body2" color="text.secondary">{l.meaning} · {l.ip}</Typography>
                  </Stack>
                ))}
              </Box>
            );
          })}
        </Box>
      )}

      {events.length > 0 && (
        <Box sx={{ ...panel, overflow: 'hidden' }}>
          <Box sx={{ p: 2.2 }}><Typography sx={{ fontWeight: 700 }}>What changed</Typography></Box>
          {events.map((e) => (
            <Stack key={e.id} direction="row" alignItems="center" gap={1.5} flexWrap="wrap" sx={{ px: 2.8, py: 1.2, borderTop: 1, borderColor: 'divider' }}>
              <Pill color={e.kind === 'listed' ? '#ef4444' : GREEN}>{e.kind === 'listed' ? 'LISTED' : 'CLEARED'}</Pill>
              <Typography variant="body2" sx={{ flex: '1 1 260px' }}><b style={{ fontFamily: MONO }}>{e.host}</b> {e.kind === 'listed' ? 'is now on' : 'is off'} {e.list_name}{e.kind === 'listed' && e.meaning ? ` (${e.meaning})` : ''}</Typography>
              <Typography variant="caption" color="text.secondary">{ago(e.created_at)}</Typography>
            </Stack>
          ))}
        </Box>
      )}
    </Stack>
  );
}

export default function BlacklistPage() {
  const [target, setTarget] = useState('');
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const run = async (e) => {
    e.preventDefault();
    if (!target.trim() || busy) return;
    setBusy(true); setError(''); setData(null);
    try { setData(await checkBlacklist(target.trim())); } catch (err) {
      setError(err?.response?.data?.detail || err?.data?.detail || err?.message || 'The check failed.');
    } finally { setBusy(false); }
  };

  return (
    <Stack spacing={2.5} className="fuzzscan-page" sx={{ p: 3 }}>
      <Box>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>Blacklist check</Typography>
        <Typography color="text.secondary" variant="body2">Is a mail server or IP address on a spam or abuse blacklist, and what to fix first</Typography>
      </Box>
      <Box component="form" onSubmit={run} sx={{ ...panel, p: 2.4 }}>
        <Stack direction="row" gap={1.5} flexWrap="wrap">
          <TextField size="small" value={target} onChange={(e) => setTarget(e.target.value)} placeholder="mail.example.com or 203.0.113.7" sx={{ flex: '1 1 320px' }} inputProps={{ 'aria-label': 'IP address or domain' }} />
          <Button type="submit" variant="contained" disabled={busy || !target.trim()} sx={{ textTransform: 'none', bgcolor: GREEN, '&:hover': { bgcolor: GREEN } }}>{busy ? 'Checking…' : 'Check'}</Button>
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>Checks 24 DNS blacklists. A list is trusted only when it passes its own test addresses, so a list that refuses this server shows as "no answer" instead of a false "not listed".</Typography>
      </Box>
      {busy && <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress sx={{ color: GREEN }} /></Box>}
      {error && <Alert severity="error">{error}</Alert>}
      {data && (
        <Stack spacing={2}>
          <Typography color="text.secondary" variant="body2">
            {data.target}: {data.results.length} address{data.results.length === 1 ? '' : 'es'} checked, {data.listed_total} listing{data.listed_total === 1 ? '' : 's'} found.
          </Typography>
          {data.results.map((r) => <IpResult key={r.ip} r={r} />)}
        </Stack>
      )}
      <Watching />
    </Stack>
  );
}
